import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  CheckCircle,
  Upload,
  Person,
  ContactPhone,
  Assignment,
} from '@mui/icons-material';
import { checkInFormService } from '@/utils/check-in-form-service';
import DocumentService from '@/utils/document-service';
import ReCaptcha from '@/components/common/ReCaptcha';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { recaptchaService } from '@/utils/recaptcha-service';

interface CheckInFormData {
  guestName: string;
  whatsappNumber: string;
  email?: string;
  instagramId?: string;
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
  dietaryRestrictions?: string;
  arrivalTime?: string;
  idDocuments: {
    frontUploadId: string;
    backUploadId: string;
  };
  termsAccepted: boolean;
}

const CheckIn: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [uploading, setUploading] = useState({ front: false, back: false });
  const [uploadErrors, setUploadErrors] = useState({ front: '', back: '' });
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState<string>('');
  const [recaptchaEnabled, setRecaptchaEnabled] = useState(false);
  
  const recaptcha = useRecaptcha({
    onError: (error) => setError(`reCAPTCHA error: ${error}`),
    onExpired: () => setError('reCAPTCHA has expired. Please verify again.'),
  });
  
  const [bookingContext, setBookingContext] = useState<any>(null);
  const [formData, setFormData] = useState<CheckInFormData>({
    guestName: '',
    whatsappNumber: '',
    email: '',
    instagramId: '',
    emergencyContact: {
      name: '',
      phone: '',
      relationship: '',
    },
    dietaryRestrictions: '',
    arrivalTime: '',
    idDocuments: {
      frontUploadId: '',
      backUploadId: '',
    },
    termsAccepted: false,
  });

  const steps = ['Personal Info', 'Contact Details', 'ID Documents', 'Review & Submit'];


  const loadRecaptchaConfig = () => {
    try {
      const enabled = recaptchaService.isEnabled();
      setRecaptchaEnabled(enabled);
      
      if (enabled) {
        const siteKey = recaptchaService.getSiteKey();
        setRecaptchaSiteKey(siteKey);
        console.log('reCAPTCHA loaded with site key:', siteKey);
      }
    } catch (error) {
      console.warn('Could not load reCAPTCHA config:', error);
      // Still enable reCAPTCHA with fallback site key
      setRecaptchaEnabled(true);
      setRecaptchaSiteKey('6LfyedcrAAAAAOOgkkqtbkgz7rVlDcj6AFFClzOL');
    }
  };

  const validateToken = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Validating token:', token);
      
      const context = await checkInFormService.validateFormAccess(token!);
      console.log('Validation response:', context);
      
      // If we get here without an error, the token is valid
      // The API returns the booking context directly
      setBookingContext({
        booking: context.booking,
        hostName: context.hostName,
        listingTitle: context.listingTitle,
        formStatus: context.formStatus,
        isValidToken: true,
        tokenExpired: false,
      });
    } catch (err) {
      console.error('Error validating token:', err);
      console.error('Error details:', {
        message: err instanceof Error ? err.message : String(err),
        tokenExpired: (err as any)?.tokenExpired,
        status: (err as any)?.status,
        response: (err as any)?.response
      });
      
      if (err instanceof Error) {
        if ((err as any).tokenExpired || err.message.includes('expired')) {
          setError('This check-in form has expired. Please contact your host for a new link.');
        } else if (err.message.includes('Invalid token')) {
          setError('Invalid check-in link. Please contact your host for assistance.');
        } else {
          setError('Unable to load check-in form. Please try again or contact your host.');
        }
      } else {
        setError('Unable to load check-in form. Please try again or contact your host.');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);
  useEffect(() => {
    // Load reCAPTCHA config immediately
    loadRecaptchaConfig();
    
    if (!token) {
      setError('Invalid check-in link');
      setLoading(false);
      return;
    }

    validateToken();
  }, [token, validateToken]);

  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof CheckInFormData] as any),
          [child]: value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0: // Personal Info + reCAPTCHA
        const basicInfo = formData.guestName.trim().length >= 2;
        const recaptchaValidation = !recaptchaEnabled || recaptcha.isVerified;
        return basicInfo && recaptchaValidation;
      case 1: // Contact Details
        return formData.whatsappNumber.trim().length >= 7;
      case 2: // ID Documents
        return Boolean(formData.idDocuments.frontUploadId && formData.idDocuments.backUploadId);
      case 3: // Review & Submit
        return formData.termsAccepted;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, side: 'front' | 'back') => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    const validation = DocumentService.validateFileType(file);
    if (!validation.valid) {
      setUploadErrors(prev => ({ ...prev, [side]: validation.error || 'Invalid file' }));
      return;
    }

    // Clear previous errors
    setUploadErrors(prev => ({ ...prev, [side]: '' }));
    setUploading(prev => ({ ...prev, [side]: true }));

    try {
      const documentType = side === 'front' ? 'id_front' : 'id_back';

      // Generate upload URL using check-in token
      const uploadData = await DocumentService.generateUploadUrl(
        token!,
        documentType,
        file.name,
        file.size,
        file.type
      );

      // Upload file to S3
      const uploadFormData = new FormData();
      Object.entries(uploadData.fields).forEach(([key, value]) => {
        uploadFormData.append(key, value);
      });
      uploadFormData.append('file', file);

      const uploadResponse = await fetch(uploadData.uploadUrl, {
        method: 'POST',
        body: uploadFormData,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status: ${uploadResponse.status}`);
      }

      // Confirm upload
      await DocumentService.confirmDocumentUpload(token!, uploadData.uploadId, {});

      // Update form data with document ID
      handleInputChange(`idDocuments.${side}UploadId`, uploadData.uploadId);

    } catch (err) {
      console.error(`Error uploading ${side} ID:`, err);
      setUploadErrors(prev => ({ 
        ...prev, 
        [side]: err instanceof Error ? err.message : 'Upload failed' 
      }));
    } finally {
      setUploading(prev => ({ ...prev, [side]: false }));
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(3) || !token) return;

    try {
      setSubmitting(true);
      setError(null);

      // Prepare form data with reCAPTCHA token
      const submitData = {
        ...formData,
        recaptchaToken: recaptcha.token,
      };

      await checkInFormService.submitCheckInForm(
        token,
        submitData,
        window.location.hostname,
        navigator.userAgent
      );

      setSuccess(true);
    } catch (err) {
      console.error('Error submitting check-in form:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit check-in form');
      
      // Reset reCAPTCHA on error
      if (recaptchaEnabled) {
        recaptcha.reset();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" gutterBottom>
              Personal Information
            </Typography>
            <TextField
              label="Full Name"
              value={formData.guestName}
              onChange={(e) => handleInputChange('guestName', e.target.value)}
              required
              fullWidth
              helperText="Enter your full name as it appears on your ID"
            />
            <TextField
              label="Estimated Arrival Time"
              type="time"
              value={formData.arrivalTime}
              onChange={(e) => handleInputChange('arrivalTime', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Optional: Let us know when you plan to arrive"
            />
            <TextField
              label="Dietary Restrictions or Allergies"
              value={formData.dietaryRestrictions}
              onChange={(e) => handleInputChange('dietaryRestrictions', e.target.value)}
              multiline
              rows={3}
              fullWidth
              helperText="Optional: Any dietary restrictions we should know about"
            />

            {/* reCAPTCHA on first step */}
            {recaptchaEnabled && recaptchaSiteKey && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Please verify that you are not a robot:
                </Typography>
                <ReCaptcha
                  siteKey={recaptchaSiteKey}
                  action="CHECKIN_FORM"
                  onVerify={recaptcha.handleVerify}
                  onError={recaptcha.handleError}
                  disabled={submitting}
                  autoExecute={true}
                />
                {recaptcha.error && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {recaptcha.error}
                  </Alert>
                )}
                {recaptcha.isVerified && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    ✓ Verification successful
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        );

      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" gutterBottom>
              Contact Information
            </Typography>
            <TextField
              label="WhatsApp Number"
              value={formData.whatsappNumber}
              onChange={(e) => handleInputChange('whatsappNumber', e.target.value)}
              required
              fullWidth
              helperText="Include country code (e.g., +1234567890)"
            />
            <TextField
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              fullWidth
              helperText="Optional: For booking confirmations and updates"
            />
            <TextField
              label="Instagram Handle"
              value={formData.instagramId}
              onChange={(e) => handleInputChange('instagramId', e.target.value)}
              fullWidth
              helperText="Optional: Your Instagram username (without @)"
            />
            
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Emergency Contact (Optional)
            </Typography>
            <TextField
              label="Emergency Contact Name"
              value={formData.emergencyContact?.name}
              onChange={(e) => handleInputChange('emergencyContact.name', e.target.value)}
              fullWidth
            />
            <TextField
              label="Emergency Contact Phone"
              value={formData.emergencyContact?.phone}
              onChange={(e) => handleInputChange('emergencyContact.phone', e.target.value)}
              fullWidth
            />
            <TextField
              label="Relationship"
              value={formData.emergencyContact?.relationship}
              onChange={(e) => handleInputChange('emergencyContact.relationship', e.target.value)}
              fullWidth
              helperText="e.g., Parent, Spouse, Friend"
            />
          </Box>
        );

      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" gutterBottom>
              ID Document Upload
            </Typography>
            <Alert severity="info">
              Please upload clear photos of both sides of your government-issued ID (passport, driver's license, etc.)
            </Alert>
            
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Front of ID
                </Typography>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  id="front-id-upload"
                  onChange={(e) => handleFileUpload(e, 'front')}
                />
                <label htmlFor="front-id-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<Upload />}
                    fullWidth
                    sx={{ mb: 2 }}
                    disabled={submitting || uploading.front}
                  >
                    {uploading.front ? 'Uploading...' : 'Upload Front of ID'}
                  </Button>
                </label>
                {formData.idDocuments.frontUploadId && (
                  <Alert severity="success" icon={<CheckCircle />}>
                    Front ID uploaded successfully
                  </Alert>
                )}
                {uploadErrors.front && (
                  <Alert severity="error">
                    {uploadErrors.front}
                  </Alert>
                )}
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Back of ID
                </Typography>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  id="back-id-upload"
                  onChange={(e) => handleFileUpload(e, 'back')}
                />
                <label htmlFor="back-id-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<Upload />}
                    fullWidth
                    sx={{ mb: 2 }}
                    disabled={submitting || uploading.back}
                  >
                    {uploading.back ? 'Uploading...' : 'Upload Back of ID'}
                  </Button>
                </label>
                {formData.idDocuments.backUploadId && (
                  <Alert severity="success" icon={<CheckCircle />}>
                    Back ID uploaded successfully
                  </Alert>
                )}
                {uploadErrors.back && (
                  <Alert severity="error">
                    {uploadErrors.back}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" gutterBottom>
              Review & Submit
            </Typography>
            
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Booking Information
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Property: {bookingContext?.listingTitle}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Host: {bookingContext?.hostName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Check-in: {bookingContext?.booking?.startDate ? new Date(bookingContext.booking.startDate).toLocaleDateString() : 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Check-out: {bookingContext?.booking?.endDate ? new Date(bookingContext.booking.endDate).toLocaleDateString() : 'N/A'}
                </Typography>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Your Information
                </Typography>
                <Typography variant="body2">Name: {formData.guestName}</Typography>
                <Typography variant="body2">WhatsApp: {formData.whatsappNumber}</Typography>
                {formData.email && <Typography variant="body2">Email: {formData.email}</Typography>}
                {formData.arrivalTime && <Typography variant="body2">Arrival Time: {formData.arrivalTime}</Typography>}
                <Typography variant="body2" color="success.main">
                  ID Documents: Uploaded ✓
                </Typography>
              </CardContent>
            </Card>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.termsAccepted}
                  onChange={(e) => handleInputChange('termsAccepted', e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the terms and conditions and confirm that all information provided is accurate
                </Typography>
              }
            />
          </Box>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress />
          <Typography>Loading check-in form...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button variant="contained" onClick={() => navigate('/')}>
            Go to Homepage
          </Button>
        </Paper>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Check-in Complete!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Thank you for completing your check-in. Your host has been notified and will be in touch with any additional details.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/')}>
            Done
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Guest Check-in
        </Typography>
        
        {bookingContext && (
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="primary">
              {bookingContext.listingTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Hosted by {bookingContext.hostName}
            </Typography>
          </Box>
        )}

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 4 }}>
          {renderStepContent(activeStep)}
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0}
          >
            Back
          </Button>
          
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!validateStep(activeStep) || submitting}
              startIcon={submitting ? <CircularProgress size={16} /> : <Assignment />}
            >
              {submitting ? 'Submitting...' : 'Submit Check-in'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!validateStep(activeStep)}
            >
              Next
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default CheckIn;